import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  useMediaQuery,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../../../auth";
import {
  getDeleteJobMutationOptions,
  getDisableJobMutationOptions,
  getFindJobQueryOptions,
  getEnableJobMutationOptions,
} from "../../../../../api/bpm/endpoints";

import { ConfirmModal } from "../../../../../components/ConfirmModal";
import { LogsModal } from "../../../../../components/LogsModal";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import theme from "../../../../../theme";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/__auth/admin/functions/$jobId/")({
  component: FunctionViewPage,
  staticData: {
    title: "routes.viewFunction",
  },
});

function FunctionViewPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { jobId } = Route.useParams();
  const tenant = user?.tenant || "default-domain";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);

  const {
    data: rawJob,
    isLoading,
    error,
  } = useQuery({ ...getFindJobQueryOptions(tenant, Number(jobId)) });

  const job = rawJob as any;

  const enableMutation = useMutation({ ...getEnableJobMutationOptions(), onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            'findJob',
            { jobId: Number(jobId), tenant },
          ],
        });
        queryClient.invalidateQueries({
          queryKey: ['listJob'],
        });
        setIsConfirmOpen(false);
      } });

  const disableMutation = useMutation({ ...getDisableJobMutationOptions(), onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            'findJob',
            { jobId: Number(jobId), tenant },
          ],
        });
        queryClient.invalidateQueries({
          queryKey: ['listJob'],
        });
        setIsConfirmOpen(false);
      } });

  const deleteMutation = useMutation({ ...getDeleteJobMutationOptions(), onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['listJob'],
        });
        navigate({ to: "/admin/functions", search: { page: 1, size: 10 } });
      } });

  const handleToggleStatus = () => {
    if (job?.enabled) {
      disableMutation.mutate({ jobId: Number(jobId), tenant: tenant });
    } else {
      enableMutation.mutate({ jobId: Number(jobId), tenant: tenant });
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate({ jobId: Number(jobId), tenant: tenant });
  };

  const isMutationLoading =
    enableMutation.isPending ||
    disableMutation.isPending ||
    deleteMutation.isPending;

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {t("admin.functions.loadError")}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            navigate({ to: "/admin/functions", search: { page: 1, size: 10 } })
          }
        >{t("admin.common.backToList")}</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Box
          sx={{
            width: 4,
            height: 40,
            bgcolor: "primary.main",
            borderRadius: 1,
            mr: 2,
          }}
        />
        <Typography variant="h5" fontWeight={600}>
          {t("admin.functions.viewData")} {job?.name}
        </Typography>
      </Box>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Dados da Função
      </Typography>

      <Stack spacing={2.5} sx={{ mb: 4 }}>
        <TextField
          label="Id"
          value={job?.id || ""}
          fullWidth
          size="small"
          slotProps={{ input: { readOnly: true } }}
        />
        <TextField
          label={t("common.name")}
          value={job?.name || ""}
          fullWidth
          size="small"
          slotProps={{ input: { readOnly: true } }}
        />
        <TextField
          label={t("admin.common.description")}
          value={job?.description || ""}
          fullWidth
          size="small"
          multiline
          rows={3}
          slotProps={{ input: { readOnly: true } }}
        />
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Box
        sx={
          isMobile
            ? { display: "flex", flexDirection: "column", gap: 1.5 }
            : { display: "flex", justifyContent: "flex-end", gap: 1.5 }
        }
      >
        <Button
          variant="contained"
          color={job?.enabled ? "error" : "success"}
          startIcon={job?.enabled ? <BlockIcon /> : <CheckCircleIcon />}
          onClick={() => setIsConfirmOpen(true)}
          disabled={isMutationLoading}
        >
          {job?.enabled ? t("common.deactivate").toUpperCase() : t("common.activate").toUpperCase()}
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setIsDeleteConfirmOpen(true)}
          disabled={isMutationLoading}
        >{t("common.delete").toUpperCase()}</Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => navigate({ to: `/admin/functions/${jobId}/edit` })}
        >{t("common.edit").toUpperCase()}</Button>
        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={() => setIsLogsModalOpen(true)}
        >
          LOG
        </Button>
        <Button
          variant="outlined"
          onClick={() =>
            navigate({ to: "/admin/functions", search: { page: 1, size: 10 } })
          }
        >{t("common.cancel").toUpperCase()}</Button>
      </Box>

      <ConfirmModal
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleToggleStatus}
        title={job?.enabled ? t("admin.functions.deactivateTitle") : t("admin.functions.activateTitle")}
        description={
          job?.enabled
            ? t("admin.functions.deactivateConfirmation", { name: job?.name })
            : t("admin.functions.activateConfirmation", { name: job?.name })
        }
        confirmText={job?.enabled ? t("common.deactivate") : t("common.activate")}
        confirmColor={job?.enabled ? "error" : "primary"}
        isLoading={isMutationLoading}
      />

      <ConfirmModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title={t("admin.functions.deleteTitle")}
        description={t("admin.functions.deleteConfirmation", { name: job?.name })}
        confirmText={t("common.delete")}
        confirmColor="error"
        isLoading={isMutationLoading}
      />

      <LogsModal
        open={isLogsModalOpen}
        onClose={() => setIsLogsModalOpen(false)}
        entityId={jobId}
        entityName={job?.name || ""}
        tenant={tenant}
        entityType="jobs"
      />
    </Container>
  );
}
